import { useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  makeStyles,
  shorthands,
  tokens,
  Text,
  Badge,
  Divider,
  Button,
  Tooltip,
} from '@fluentui/react-components';
import {
  Person24Regular,
  Laptop24Regular,
  Database24Regular,
  Apps24Regular,
  Server24Regular,
  Globe24Regular,
  Board24Regular,
  ShieldCheckmark24Regular,
  Building24Regular,
  WeatherSunny24Regular,
  WeatherMoon24Regular,
  Library24Regular,
} from '@fluentui/react-icons';
import { PILLARS } from '../theme/pillars';
import { CustomerPicker } from './CustomerPicker';
import type { MsPillar } from '../api/client';

const useStyles = makeStyles({
  shell: {
    display: 'grid',
    // minmax(0, 1fr) rather than 1fr. A bare 1fr track refuses to shrink below
    // its content, so one wide table widens the whole page. This is the single
    // most common cause of an unexpected horizontal scrollbar in a grid layout.
    gridTemplateColumns: '280px minmax(0, 1fr)',
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  rail: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRight('1px', 'solid', tokens.colorNeutralStroke2),
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalM),
    rowGap: tokens.spacingVerticalXS,
    // Sticks to the viewport, with its own scroll when the nav outgrows the
    // window. This works only while no ancestor is itself a scroll container,
    // which is why html and body deliberately carry no overflow rule.
    position: 'sticky',
    top: 0,
    height: '100vh',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  brand: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXXS,
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalS),
  },
  sectionLabel: {
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalS, tokens.spacingVerticalXS),
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
    ...shorthands.padding(tokens.spacingVerticalSNudge, tokens.spacingHorizontalS),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    color: tokens.colorNeutralForeground1,
    textDecorationLine: 'none',
    fontSize: tokens.fontSizeBase300,
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  navItemActive: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  navLabel: { flexGrow: 1 },
  main: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  content: {
    ...shorthands.padding(tokens.spacingVerticalXXL, tokens.spacingHorizontalXXL),
    maxWidth: '1400px',
    width: '100%',
    minWidth: 0,
  },
  railFooter: {
    marginTop: 'auto',
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalS),
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXS,
  },
  residency: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    lineHeight: tokens.lineHeightBase200,
  },
});

const PILLAR_ICONS: Record<MsPillar, ReactNode> = {
  identity: <Person24Regular />,
  endpoints: <Laptop24Regular />,
  data: <Database24Regular />,
  apps: <Apps24Regular />,
  infrastructure: <Server24Regular />,
  network: <Globe24Regular />,
};

interface NavLinkProps {
  to: string;
  icon: ReactNode;
  label: string;
  count?: number | undefined;
}

function NavLink({ to, icon, label, count }: NavLinkProps) {
  const styles = useStyles();
  const location = useLocation();
  const active = location.pathname === to || location.pathname.startsWith(`${to}/`);

  return (
    <Link
      to={to}
      className={`${styles.navItem} ${active ? styles.navItemActive : ''}`}
      aria-current={active ? 'page' : undefined}
    >
      {icon}
      <span className={styles.navLabel}>{label}</span>
      {count !== undefined && count > 0 && (
        <Badge appearance="filled" color="informative" size="small">
          {count}
        </Badge>
      )}
    </Link>
  );
}

export interface ShellProps {
  children: ReactNode;
  stampRegion?: string | undefined;
  findingsByPillar?: Partial<Record<MsPillar, number>> | undefined;
  onToggleTheme: () => void;
  isDark: boolean;
}

export function Shell({
  children,
  stampRegion,
  findingsByPillar,
  onToggleTheme,
  isDark,
}: ShellProps) {
  const styles = useStyles();
  const [themeLabel] = useState('Switch theme');

  return (
    <div className={styles.shell}>
      <nav className={styles.rail} aria-label="Primary">
        <div className={styles.brand}>
          <Text weight="semibold" size={400}>
            Cloud Security Assessment
          </Text>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
            SoftwareOne
          </Text>
        </div>

        <Divider />

        <div className={styles.sectionLabel}>Overview</div>
        <NavLink to="/" icon={<Board24Regular />} label="Dashboard" />
        <NavLink to="/tenants" icon={<Building24Regular />} label="Customers" />

        <div className={styles.sectionLabel}>Zero Trust pillars</div>
        {PILLARS.map((pillar) => (
          <NavLink
            key={pillar.id}
            to={pillar.route}
            icon={PILLAR_ICONS[pillar.id]}
            label={pillar.label}
            count={findingsByPillar?.[pillar.id]}
          />
        ))}

        <div className={styles.sectionLabel}>Reference</div>
        <NavLink to="/frameworks" icon={<ShieldCheckmark24Regular />} label="Frameworks" />
        <NavLink to="/catalogue" icon={<Library24Regular />} label="Control catalogue" />

        <div className={styles.railFooter}>
          <Tooltip content={themeLabel} relationship="label">
            <Button
              appearance="subtle"
              icon={isDark ? <WeatherSunny24Regular /> : <WeatherMoon24Regular />}
              onClick={onToggleTheme}
            >
              {isDark ? 'Light theme' : 'Dark theme'}
            </Button>
          </Tooltip>

          {stampRegion && (
            <Text className={styles.residency}>
              Data region: <strong>{stampRegion}</strong>. Customer data registered here
              stays in this region.
            </Text>
          )}
        </div>
      </nav>

      <main className={styles.main}>
        {/* Which customer is on screen is the most important context in the
            interface, so it sits above everything and stays there. */}
        <CustomerPicker />
        <div className={styles.content}>{children}</div>
      </main>
    </div>
  );
}
